import React, { useState, useCallback } from 'react';
import { Shield, Search, Filter, AlertTriangle, AlertOctagon, AlertCircle, ChevronRight, ArrowUpDown } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { TablePagination } from '../../ui/TablePagination';
import { Link } from 'react-router-dom';
import { FindingsFilters } from '../../findings/FindingsFilters';
import { severityOptions, statusOptions, timeframeOptions } from '../../../data/findings-filters';

interface Finding {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: string;
  cvss: number;
  discoveredAt: string;
  lastUpdated: string;
}

interface AssetFindingsProps {
  findings: any[];
  paginationInfo: {
    count: number;
    next: string | null;
    previous: string | null;
  };
  onPageChange: (page: number) => void;
  currentPage: number;
  itemsPerPage: number;
  onFilterChange?: (filters: {
    search?: string;
    severity?: string;
    status?: string;
    createdLast?: number;
  }) => void;
}

export const AssetFindings: React.FC<AssetFindingsProps> = ({ 
  findings, 
  paginationInfo, 
  onPageChange, 
  currentPage, 
  itemsPerPage,
  onFilterChange 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    onFilterChange?.({
      search: query,
      severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
    });
  }, [selectedSeverity, selectedStatus, onFilterChange]);

  const handleSeverityChange = useCallback((severity: string) => {
    setSelectedSeverity(severity);
    onFilterChange?.({
      search: searchQuery,
      severity: severity !== 'all' ? severity : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
    });
  }, [searchQuery, selectedStatus, onFilterChange]);

  const handleStatusChange = useCallback((status: string) => {
    setSelectedStatus(status);
    onFilterChange?.({
      search: searchQuery,
      severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
      status: status !== 'all' ? status : undefined,
    });
  }, [searchQuery, selectedSeverity, onFilterChange]);

  const handleTimeframeChange = useCallback((timeframe: string) => {
    setSelectedTimeframe(timeframe);
    let createdLast: number | undefined;
    switch (timeframe) {
      case 'today':
        createdLast = 1;
        break;
      case 'week':
        createdLast = 7;
        break;
      case 'month':
        createdLast = 30;
        break;
      case 'quarter':
        createdLast = 90;
        break;
      default:
        createdLast = undefined;
    }
    onFilterChange?.({
      search: searchQuery,
      severity: selectedSeverity !== 'all' ? selectedSeverity : undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      createdLast,
    });
  }, [searchQuery, selectedSeverity, selectedStatus, onFilterChange]);

  const handleResetFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedSeverity('all');
    setSelectedStatus('all');
    setSelectedTimeframe('all');
    onFilterChange?.({});
  }, [onFilterChange]);

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertOctagon className="h-5 w-5 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'medium':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <Shield className="h-5 w-5 text-emerald-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const sortedFindings = [...findings].sort((a, b) => {
    return sortDirection === 'desc' 
      ? new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
      : new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
  });

  return (
    <div className="space-y-4">
      <FindingsFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedSeverity={selectedSeverity}
        onSeverityChange={handleSeverityChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedTimeframe={selectedTimeframe}
        onTimeframeChange={handleTimeframeChange}
        selectedTags={[]}
        onTagChange={() => { }}
        selectedAssignees={[]}
        onAssigneeChange={() => { }}
        onReset={handleResetFilters}
        onExport={() => { }}
      />
      {sortedFindings.length > 0 ? (
        <>
          <div className="space-y-2">
            {sortedFindings.map((finding) => (
              <div
                key={finding.id}
                className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 group hover:bg-[#0F1629]/80 transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getSeverityIcon(finding.severity)}
                    <div>
                      <Link
                        to={`/findings/${finding.id}`}
                        className="text-gray-200 font-medium hover:text-blue-400 transition-colors flex items-center group"
                      >
                        {finding.title}
                        <ChevronRight className="h-4 w-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-1 transition-all" />
                      </Link>
                      <div className="text-xs text-gray-400 mt-1">{finding.overview}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={finding.severity}>{finding.severity.toUpperCase()}</Badge>
                    <Badge variant={finding.status}>{finding.status.replace('_', ' ').toUpperCase()}</Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <TablePagination
            currentPage={currentPage}
            totalItems={paginationInfo.count}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            paginationInfo={paginationInfo}
            itemName="findings"
          />
        </>

      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-4 text-gray-400 flex items-center justify-center">
              No findings found
            </div>
          </div>
        </div>
      )}
    </div>
  );
};